import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../design-system/icons';
import { Dialog, DialogActions } from '../../../design-system/Overlay';
import { Empty, Loading } from '../../../states/States';
import type { FolderNode } from './folder-source';

/**
 * Nom de dossier saisi dans la carte même (création, renommage) : Entrée valide, Échap annule ;
 * quitter le champ valide un nom changé et non vide, sinon annule. Un refus du serveur garde la saisie.
 */
export function FolderNameInput({ label, initial, onSubmit, onCancel }: { label: string; initial: string; onSubmit: (name: string) => Promise<boolean>; onCancel: () => void }) {
  const [name, setName] = useState(initial);
  const busy = useRef(false);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);
  const submit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (busy.current) return;
    if (trimmed === '' || trimmed === initial) {
      onCancel();
      return;
    }
    busy.current = true;
    const ok = await onSubmit(trimmed);
    busy.current = false;
    if (!ok) field.current?.focus();
  };
  return (
    <span className="ay-folders__name-edit" onClick={(event) => event.stopPropagation()}>
      <input
        ref={field}
        aria-label={label}
        value={name}
        maxLength={200}
        placeholder="Nom du dossier"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => void submit()}
      />
      <button type="button" aria-label="Valider" onMouseDown={(event) => event.preventDefault()} onClick={() => void submit()}><Icon name="check" size={14} /></button>
      <button type="button" aria-label="Annuler" onMouseDown={(event) => event.preventDefault()} onClick={onCancel}><Icon name="close" size={14} /></button>
    </span>
  );
}

/**
 * « Déplacer vers… » : l'arbre des dossiers actifs de la source, filtrable ; un clic choisit, il ne
 * déplace pas — le pied dit où l'on va et « Déplacer ici » valide. Le dossier actuel est grisé et
 * nommé ; un dossier ne va ni sous lui-même ni sous l'un de ses sous-dossiers (le serveur refuserait).
 */
export function MoveToFolderDialog({ load, rootLabel, subtitle, label, from, exclude, onClose, onMove }: { load: () => Promise<FolderNode[]>; rootLabel: string; subtitle: string; label: string; from: string | null; exclude?: readonly string[]; onClose: () => void; onMove: (folderId: string | null) => void }) {
  const [folders, setFolders] = useState<FolderNode[] | null>(null);
  const [filter, setFilter] = useState('');
  const [choice, setChoice] = useState<string | null | undefined>(undefined);
  const loader = useRef(load);
  useEffect(() => {
    let alive = true;
    loader
      .current()
      .then((found) => alive && setFolders(found))
      .catch(() => alive && setFolders([]));
    return () => {
      alive = false;
    };
  }, []);
  const rows = useMemo(() => {
    if (folders === null) return [];
    const excluded = new Set(exclude ?? []);
    const byId = new Map(folders.map((folder) => [folder.id, folder]));
    const children = new Map<string, FolderNode[]>();
    for (const folder of folders) {
      const parent = folder.parent_id === null || !byId.has(folder.parent_id) ? '' : folder.parent_id;
      children.set(parent, [...(children.get(parent) ?? []), folder]);
    }
    const pathOf = (folder: FolderNode, depth = 0): string => {
      const parent = folder.parent_id === null ? undefined : byId.get(folder.parent_id);
      return parent === undefined || depth > 20 ? folder.name : `${pathOf(parent, depth + 1)} › ${folder.name}`;
    };
    const fold = (value: string): string => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const wanted = fold(filter.trim());
    const out: Array<{ folder: FolderNode; depth: number; path: string }> = [];
    const walk = (parent: string, depth: number): void => {
      for (const folder of [...(children.get(parent) ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr'))) {
        // Un dossier déplacé n'a pas de place sous lui-même : ni lui, ni ses sous-dossiers.
        if (excluded.has(folder.id)) continue;
        const path = pathOf(folder);
        if (wanted === '' || fold(path).includes(wanted)) out.push({ folder, depth: wanted === '' ? depth : 0, path });
        if (depth < 20) walk(folder.id, depth + 1);
      }
    };
    walk('', 0);
    return out;
  }, [folders, filter, exclude]);
  const chosen = choice === undefined ? undefined : choice === null ? rootLabel : rows.find((row) => row.folder.id === choice)?.path;
  return (
    <Dialog
      title={`Déplacer « ${label} »`}
      subtitle={subtitle}
      open
      size="list"
      onClose={onClose}
      note={chosen === undefined ? 'Choisissez un dossier.' : `Vers « ${chosen} »`}
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: 'Déplacer ici', disabled: choice === undefined, onClick: () => choice !== undefined && onMove(choice) }} />}
    >
      <div className="ay-folders__move">
        <label className="ay-folders__search">
          <Icon name="search" size={14} />
          <input type="search" value={filter} placeholder="Chercher un dossier…" aria-label="Chercher un dossier" onChange={(event) => setFilter(event.target.value)} />
        </label>
        <div className="ay-folders__destinations" role="listbox" aria-label="Destinations">
          {folders === null && <Loading />}
          {filter.trim() === '' && (
            <button type="button" role="option" aria-selected={choice === null} aria-disabled={from === null ? true : undefined} className="ay-folders__destination" onClick={() => from !== null && setChoice(null)}>
              <Icon name="home" size={16} />
              <span className="ay-folders__destination-name">{rootLabel}</span>
              {from === null && <span className="ay-folders__note">Dossier actuel</span>}
              {choice === null && <Icon name="check" size={16} />}
            </button>
          )}
          {rows.map(({ folder, depth, path }) => {
            const current = folder.id === from;
            return (
              <button key={folder.id} type="button" role="option" aria-selected={choice === folder.id} aria-disabled={current ? true : undefined} className="ay-folders__destination" style={{ paddingLeft: 10 + depth * 20 }} title={path} onClick={() => !current && setChoice(folder.id)} onDoubleClick={() => !current && onMove(folder.id)}>
                {depth > 0 && <Icon name="chevron" size={14} />}
                <Icon name="folder" size={16} />
                <span className="ay-folders__destination-name">{filter.trim() === '' ? folder.name : path}</span>
                {folder.locked && <span className="ay-folders__system">Système</span>}
                {current && <span className="ay-folders__note">Dossier actuel</span>}
                {choice === folder.id && <Icon name="check" size={16} />}
              </button>
            );
          })}
          {folders !== null && rows.length === 0 && <Empty label={filter.trim() === '' ? 'Aucun dossier.' : 'Aucun dossier ne correspond.'} />}
        </div>
      </div>
    </Dialog>
  );
}
